import React, { useState } from 'react';
import AppHeader from '../ui/AppHeader';
import './AdminPage.css';

const AdminPage = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Simple password authentication (you can replace with more secure auth)
  const ADMIN_PASSWORD = process.env.REACT_APP_ADMIN_PASSWORD || 'wineapp2025';
  
  // Your Railway backend URL
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'https://your-railway-app.railway.app';

  const handleLogin = (e) => {
    e.preventDefault();
    if (password === ADMIN_PASSWORD) {
      setIsAuthenticated(true);
      setError('');
      fetchStats();
    } else {
      setError('Invalid password');
    }
  };

  const fetchStats = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/stats`);
      if (response.ok) {
        const data = await response.json();
        setStats(data.stats);
      } else {
        setError('Failed to load admin data');
      }
    } catch (err) {
      setError('Connection error: ' + err.message);
    }
    setLoading(false);
  };

  const downloadData = async (type) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/download-csv?type=${type}`);
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          // Create and download file
          const blob = new Blob([data.data], { type: 'text/csv' });
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.style.display = 'none';
          a.href = url;
          a.download = data.filename;
          document.body.appendChild(a);
          a.click();
          window.URL.revokeObjectURL(url);
          document.body.removeChild(a);
        } else {
          setError(`Download failed: ${data.error}`);
        }
      } else {
        setError('Failed to download data');
      }
    } catch (err) {
      setError('Download error: ' + err.message);
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="admin-login">
        <div className="login-container">
          <h1>🍷 WineApp Admin</h1>
          <form onSubmit={handleLogin}>
            <div className="form-group">
              <label htmlFor="password">Admin Password:</label>
              <input
                type="password"
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </div>
            {error && <div className="error">{error}</div>}
            <button type="submit" className="login-btn">Login</button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-dashboard">
      <AppHeader />
      <div className="admin-header">
        <h1>🍷 WineApp Admin Dashboard</h1>
        <button 
          onClick={() => setIsAuthenticated(false)} 
          className="logout-btn"
        >
          Logout
        </button>
      </div>

      {loading && <div className="loading">Loading...</div>}
      {error && <div className="error">{error}</div>}

      {stats && (
        <div className="admin-content">
          <div className="stats-section">
            <h2>📊 Data Statistics</h2>
            <div className="stats-grid">
              <div className="stat-card">
                <div className="stat-number">
                  {stats.restaurantCount || 0}
                </div>
                <div className="stat-label">Restaurant Decisions</div>
                <div className="stat-description">User wine choices at restaurants</div>
              </div>
              <div className="stat-card">
                <div className="stat-number">
                  {stats.homeCount || 0}
                </div>
                <div className="stat-label">Home Cooking Decisions</div>
                <div className="stat-description">Wine recommendations for home</div>
              </div>
              <div className="stat-card">
                <div className="stat-number">
                  {stats.totalCount || 0}
                </div>
                <div className="stat-label">Total Interactions</div>
                <div className="stat-description">All user decisions tracked</div>
              </div>
            </div>
          </div>

          <div className="download-section">
            <h3>📥 Export Data</h3>
            <p>Download user interaction data for analysis and insights:</p>
            <div className="download-actions">
              <button 
                onClick={() => downloadData('restaurant')} 
                className="download-btn restaurant"
              >
                📊 Download Restaurant Data (CSV)
              </button>
              <button 
                onClick={() => downloadData('home')} 
                className="download-btn home"
              >
                🏠 Download Home Data (CSV)
              </button>
            </div>
          </div>

          <div className="data-info">
            <h4>📋 Data Includes:</h4>
            <div className="info-grid">
              <div className="info-item">
                <strong>User Preferences:</strong> Wine types, food pairings, price ranges
              </div>
              <div className="info-item">
                <strong>Decisions:</strong> Purchase intentions and wine selections
              </div>
              <div className="info-item">
                <strong>Timestamps:</strong> When interactions occurred
              </div>
              <div className="info-item">
                <strong>Context:</strong> Restaurant names and dining preferences
              </div>
            </div>
          </div>

          <div className="quick-actions">
            <button onClick={fetchStats} className="refresh-btn">
              🔄 Refresh Data
            </button>
            <div className="last-updated">
              Last updated: {stats.lastUpdated ? new Date(stats.lastUpdated).toLocaleString() : 'Just now'}
            </div>
          </div>

          <div className="info-section">
            <h4>ℹ️ System Information</h4>
            <p><strong>Backend API:</strong> {BACKEND_URL}</p>
            <p><strong>Frontend:</strong> {window.location.origin}</p>
            <p><strong>Data Storage:</strong> SQLite database with persistent storage</p>
            <p><strong>Security:</strong> Admin-only access with password protection</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPage;
